import { AxiosResponse, AxiosStatic } from "axios";
import { IPatchPayload } from "../../components/course-author/helpers/patches";
import { ICourseStyle } from "../../components/course-author/types";
import { IFullCourse } from "../../components/course-author/types";
import { AxiosInterceptedResult } from "../interceptors";

export interface ICourseAuthorApiDependencies {
  axios: AxiosStatic;
  saltcomplianceApiRoot: string;
}

export interface IImportReport {
  parsingFailure: Nullable<string>;
  nextCsv: string;
  nextCsvName: string;
  numFailures: number;
  numMissing: number;
  numUpdates: number;
  numEqualValues: number;
  numPatches: number;
}

export interface ICourseAuthorApi {
  patchCourse: (accountId: string, courseId: string, patchPayload: IPatchPayload) => Promise<AxiosResponse>;
  resolve: (requestData: string[], accountId: string) => Promise<AxiosResponse>;
  uploadFile: (
    files: File,
    accountId: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
  uploadViaUrl: (
    link: string,
    accountId: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
  createStyle: (activeUser: string, activeOrg: string, style: ICourseStyle) => Promise<AxiosResponse>;
  getOrCreateCourseInstByCourseByAccount: (courseId: string, accountId: string) => Promise<AxiosResponse<string>>;
  patchStyle: (
    activeUser: string,
    activeOrg: string,
    styleId: string,
    patchPayload: IPatchPayload
  ) => Promise<AxiosResponse>;
  migrateContentToHtml: (courseId: string, accountId: string) => Promise<AxiosResponse<IFullCourse>>;
  uploadTranslationsCsv: (
    courseId: string,
    accountId: string,
    file: File
  ) => Promise<AxiosInterceptedResult<IImportReport>>;
}

export interface IFileUploadResponseData {
  mediaId: string;
  name: string;
}

export class CourseAuthorApi implements ICourseAuthorApi {
  private readonly apiRoot: string;
  private readonly axios: AxiosStatic;

  constructor(dependencies: ICourseAuthorApiDependencies) {
    this.apiRoot = dependencies.saltcomplianceApiRoot;
    this.axios = dependencies.axios;
  }

  public createStyle = (activeUser: string, activeOrg: string, style: ICourseStyle) => {
    return this.axios.post(
      `${this.apiRoot}/styles`,
      {
        ...style.state,
        versionId: 0,
      },
      {
        params: { activeUser, activeOrg },
      }
    );
  };

  public patchCourse = (accountId: string, courseId: string, patchPayload: IPatchPayload) => {
    return this.axios.post(`${this.apiRoot}/courses/${courseId}/_patches`, patchPayload, {
      params: { activeUser: accountId },
    });
  };

  public patchStyle = (activeUser: string, activeOrg: string, styleId: string, patchPayload: IPatchPayload) => {
    return this.axios.post(`${this.apiRoot}/styles/${styleId}/_patches`, patchPayload, {
      params: { activeUser, activeOrg },
    });
  };

  public resolve = (requestData: string[], accountId: string) => {
    return this.axios.post(`${this.apiRoot}/bulk/resolve`, requestData, {
      params: { activeUser: accountId },
    });
  };

  public uploadFile = (file: File, accountId: string, onUploadProgress?: (progressEvent: ProgressEvent) => void) => {
    const formData = new FormData();

    formData.append("files[]", file);
    return this.axios.post(`${this.apiRoot}/media/_upload?accept=text/plain&activeUser=${accountId}`, formData, {
      headers: {
        Accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    }) as any;
  };

  public uploadViaUrl = (
    link: string,
    activeUser: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ) => {
    return this.axios.post(`${this.apiRoot}/media/_uploadViaUrl`, link, {
      onUploadProgress,
      params: { activeUser },
    }) as any;
  };

  public migrateContentToHtml = (courseId: string, accountId: string) => {
    return this.axios.post(`${this.apiRoot}/courses/migration/markdown/${courseId}`, undefined, {
      params: { activeUser: accountId },
    });
  };

  public getOrCreateCourseInstByCourseByAccount = (courseId: string, accountId: string) => {
    return this.axios.post(
      `${this.apiRoot}/getOrCreateCourseInstId/course/${courseId}/account/${accountId}`,
      undefined,
      {
        params: { activeUser: accountId },
      }
    );
  };

  public uploadTranslationsCsv = (courseId: string, activeUser: string, file: File) => {
    const formData = new FormData();
    formData.append("translations.csv", file, "translations.csv");
    return this.axios.patch(`${this.apiRoot}/courses/${courseId}/_localization/translations.csv`, formData, {
      params: { activeUser },
    }) as Promise<AxiosInterceptedResult<IImportReport>>;
  };
}
